import React, { useState } from 'react'
import { marmitonApi } from '../lib/api.js'

/**
 * MarmitonSuggestions — drawer/panel that fetches dish ideas from Marmiton
 * and lets the user add one directly to the planner.
 *
 * Props:
 *   onAddMeal: (mealName: string) => void   — called when user picks a dish
 *   onClose:   () => void
 */
export function MarmitonSuggestions({ onAddMeal, onClose }) {
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(null) // slotName being added

  async function fetchSuggestions() {
    setLoading(true)
    setError(null)
    try {
      const data = await marmitonApi.getSuggestions()
      setSuggestions(data.suggestions ?? [])
      setFetched(true)
    } catch (e) {
      setError('Impossible de récupérer les suggestions Marmiton.')
    } finally {
      setLoading(false)
    }
  }

  async function handleAdd(suggestion) {
    setAdding(suggestion.name)
    try {
      await onAddMeal(suggestion.name)
    } finally {
      setAdding(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Marmiton orange accent */}
            <span className="text-2xl">🍴</span>
            <div>
              <h2 className="text-base font-bold text-gray-900">Idées Marmiton</h2>
              <p className="text-xs text-gray-400">Qu'est-ce qu'on mange ce soir ?</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {/* Initial CTA */}
          {!fetched && !loading && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="text-5xl">🥘</div>
              <p className="text-sm text-gray-500 text-center">
                Charge les suggestions du jour depuis Marmiton
              </p>
              <button
                onClick={fetchSuggestions}
                className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl text-sm transition-colors"
              >
                Voir les idées du moment
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-gray-400">Chargement depuis Marmiton…</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="py-8 text-center">
              <p className="text-sm text-red-500 mb-3">{error}</p>
              <button
                onClick={fetchSuggestions}
                className="text-xs text-orange-500 underline hover:no-underline"
              >
                Réessayer
              </button>
            </div>
          )}

          {/* Results */}
          {fetched && !loading && suggestions.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">
              Aucune suggestion trouvée. Marmiton a peut-être changé sa mise en page.
            </p>
          )}

          {fetched && suggestions.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-500 font-medium">
                  {suggestions.length} idées trouvées
                </p>
                <button
                  onClick={fetchSuggestions}
                  className="text-xs text-orange-500 hover:underline"
                >
                  ↺ Actualiser
                </button>
              </div>

              {suggestions.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 border border-gray-100 rounded-xl p-3 hover:border-orange-200 hover:bg-orange-50 transition-colors group"
                >
                  {/* Thumbnail */}
                  {s.imageUrl ? (
                    <img
                      src={s.imageUrl}
                      alt={s.name}
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 text-xl">
                      🍽️
                    </div>
                  )}

                  {/* Name + link */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 leading-tight line-clamp-2">
                      {s.name}
                    </p>
                    {s.url && (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-orange-400 hover:text-orange-600 hover:underline mt-0.5 inline-block"
                      >
                        Voir la recette ↗
                      </a>
                    )}
                  </div>

                  {/* Add button */}
                  <button
                    onClick={() => handleAdd(s)}
                    disabled={adding === s.name}
                    className="flex-shrink-0 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-wait"
                  >
                    {adding === s.name ? '…' : '+ Ajouter'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer link */}
        <div className="px-5 py-3 border-t border-gray-100 text-center">
          <a
            href="https://www.marmiton.org/qu-est-ce-qu-on-mange-ce-soir-sc29.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-400 hover:text-orange-500 transition-colors"
          >
            Voir toutes les suggestions sur Marmiton ↗
          </a>
        </div>
      </div>
    </div>
  )
}
